import React from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import type { EmojiPenaltyResponse, EmojiPenaltyVerdict } from "../../api/types";

/**
 * Tells a racer about their own no-emoji penalty (aotdrace-2.1): before they
 * tap (`upcoming`, what a tap today would get) and while they wait for the art
 * (`today`, what their tap got). Renders nothing when the rule doesn't touch
 * them. The rule in one breath: no emojis staged + already made an AOTD with
 * none → +450ms; racing the next day waives it; skipping a day brings it back.
 */
export interface EmojiPenaltyNoteProps {
  penalty?: EmojiPenaltyResponse;
  /** "upcoming" on the countdown, "today" once they've tapped. */
  moment: "upcoming" | "today";
}

const Reveal = styled(motion.div)`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  overflow: hidden;
`;

const Card = styled.div<{ $tone: "warn" | "calm" }>`
  margin-top: 14px;
  padding: 10px 14px;
  border-radius: 10px;
  text-align: left;
  background: ${(p) => (p.$tone === "warn" ? "#fffbeb" : "#f9fafb")};
  border: 1px solid ${(p) => (p.$tone === "warn" ? "#fcd34d" : "#e5e7eb")};
`;

const Title = styled.div<{ $tone: "warn" | "calm" }>`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${(p) => (p.$tone === "warn" ? "#92400e" : "#374151")};
  font-variant-numeric: tabular-nums;
`;

// A div, not a p: `.art-of-the-day p` (aotd.css) would override the size.
const Body = styled.div`
  margin: 4px 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #57534e;
`;

function copyFor(
  moment: "upcoming" | "today",
  verdict: EmojiPenaltyVerdict,
  ms: number,
): { tone: "warn" | "calm"; title: string; body: string } | null {
  if (verdict === "none") return null;
  if (moment === "upcoming") {
    return verdict === "applied"
      ? {
          tone: "warn",
          title: `Your tap today gets +${ms}ms`,
          body:
            "No emojis staged for your next AOTD, and you already made one without them. " +
            "Race again tomorrow and it's dropped; skip a day and it's back. " +
            "Win and pick emojis on your art to clear it for good.",
        }
      : {
          tone: "calm",
          title: `No +${ms}ms today`,
          body:
            "Still no emojis staged, but you raced yesterday, so the penalty is waived. " +
            "Skip a day and it's back.",
        };
  }
  return verdict === "applied"
    ? {
        tone: "warn",
        title: `+${ms}ms was added to your time`,
        body:
          "You raced with no emojis staged for your next AOTD. Race again tomorrow and it " +
          "won't apply; skip a day and it's back.",
      }
    : {
        tone: "calm",
        title: "No penalty this time",
        body:
          `No emojis staged, but you raced yesterday too, so the +${ms}ms was waived. ` +
          "Skip a day and it's back.",
      };
}

export const EmojiPenaltyNote: React.FC<EmojiPenaltyNoteProps> = ({ penalty, moment }) => {
  const status = moment === "upcoming" ? penalty?.upcoming : penalty?.today;
  const copy = status ? copyFor(moment, status.verdict, penalty?.penaltyMs ?? 450) : null;

  return (
    <AnimatePresence initial={false}>
      {copy ? (
        <Reveal
          key={`${moment}-${status?.verdict}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Card $tone={copy.tone} role="status">
            <Title $tone={copy.tone}>{copy.title}</Title>
            <Body>{copy.body}</Body>
          </Card>
        </Reveal>
      ) : null}
    </AnimatePresence>
  );
};

export default EmojiPenaltyNote;
